import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { Badge, Button, Callout, Card, Heading, PageHead, Text } from '../ui/index.js';
import { PLANS, type AccessSource } from '@goodbuild/shared';
import { api } from '../api/client.js';
import { useAccess } from '../api/hooks.js';
import { Focusable, FocusZone } from '../components/focus/Focusable.js';
import { FocusableTextField } from '../components/focus/OnScreenKeyboard.js';
import { Loading } from '../components/layout/Loading.js';
import { formatDate, formatPrice } from '../lib/format.js';
import { PLAN_INTERVAL_LABELS, PLAN_TIER_LABELS } from '../lib/planLabels.js';
import { openExternal, signOut, signOutEverywhere } from '../auth/session.js';
import { LEGAL } from '../lib/legal.js';
import { errorMessage } from '../lib/errors.js';
import { BillingCard } from '../components/account/BillingCard.js';
import { DiscordLink } from '../components/account/DiscordLink.js';

export const Route = createFileRoute('/account')({
  component: Account,
  /**
   * Where every link comes back to.
   *
   * Discord on `?discord=`, Steam and Epic on `?linked=<store>&result=<...>`. Three services,
   * one screen, because linking is a thing an account has rather than a thing the studio or a
   * game has — and a redirect that landed somewhere else would leave the person who started it
   * to find their way back.
   */
  validateSearch: (
    search: Record<string, unknown>,
  ): { discord?: string; linked?: string; result?: string } => ({
    ...(typeof search.discord === 'string' ? { discord: search.discord } : {}),
    ...(typeof search.linked === 'string' ? { linked: search.linked } : {}),
    ...(typeof search.result === 'string' ? { result: search.result } : {}),
  }),
});

function accessFromLabel(source: AccessSource): string {
  switch (source) {
    case 'membership':
      return 'Your own membership';
    case 'staff':
      return 'You are on the team';
  }
}

function Account() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const { me, signedIn, hasAccess } = useAccess();
  const [error, setError] = React.useState<string | null>(null);
  const [deleteText, setDeleteText] = React.useState('');
  const [confirmDelete, setConfirmDelete] = React.useState(false);

  const deleteAccount = useMutation({
    mutationFn: () => api.delete('/me'),
    onSuccess: async () => {
      await signOut();
    },
    // Without this a refused delete left the screen exactly as it was, which reads as the
    // button doing nothing.
    onError: (err) => setError(errorMessage(err)),
  });

  if (!signedIn) {
    return (
      <div className="gb-center">
        <Focusable
          autoFocus
          onSelect={() => void navigate({ to: '/signin' })}
          selectLabel="Sign in"
        >
          <Button size="3">Sign in</Button>
        </Focusable>
      </div>
    );
  }
  if (!me) return <Loading />;

  const membership = me.membership;
  const plan = membership ? PLANS[membership.planKey] : null;

  return (
    <FocusZone focusKey="account" direction="column" className="gb-stack">
      <PageHead eyebrow="Your membership" title="Account." />

      {error ? (
        <Callout.Root color="danger">
          <Callout.Description>{error}</Callout.Description>
        </Callout.Root>
      ) : null}

      <Card size="3">
        <Heading size="4">Plan</Heading>

        {me.access.source ? (
          <Text render={<p />} size="2" color="gray">
            {accessFromLabel(me.access.source)}
          </Text>
        ) : null}

        {plan && membership ? (
          <>
            <div className="gb-row">
              <Text size="3">
                {PLAN_TIER_LABELS[plan.tier]} · {formatPrice(plan.priceCents)}{' '}
                {PLAN_INTERVAL_LABELS[plan.interval]}
              </Text>
              {membership.cancelAtPeriodEnd ? (
                <Badge color="amber">
                  {`Ends on ${membership.currentPeriodEnd ? formatDate(membership.currentPeriodEnd) : ''}`}
                </Badge>
              ) : null}
            </div>
            {membership.currentPeriodEnd && !membership.cancelAtPeriodEnd ? (
              <Text size="2" color="gray">
                Next payment: {formatDate(membership.currentPeriodEnd)}
              </Text>
            ) : null}
          </>
        ) : (
          <Text render={<p />} color="gray">
            You do not have a membership.
          </Text>
        )}
      </Card>

      {membership ? <BillingCard membership={membership} /> : null}

      <DiscordLink hasAccess={hasAccess} status={search.discord} />

      {/*
        Support, on the account screen rather than only in the legal pages.
 
        A reachable support address has to be visible from inside the product, not just on the
        terms page somebody reads once. It is also a condition of being allowed to take
        payments at all: the merchant of record requires it on the public site AND in the user
        dashboard, and this screen is the dashboard.
      */}
      <Card size="3">
        <Heading size="4">Help</Heading>
        <Text render={<p />} size="2" color="gray">
          {`Something wrong, or a question about your membership? Write to ${LEGAL.contactEmail} and we will answer within three working days.`}
        </Text>
        <Focusable
          onSelect={() => void openExternal(`mailto:${LEGAL.contactEmail}`)}
          selectLabel="Email support"
        >
          <Button size="2" variant="surface">
            {`Email ${LEGAL.contactEmail}`}
          </Button>
        </Focusable>
      </Card>

      <Card size="3">
        <div className="gb-stack">
          <Focusable onSelect={() => void signOut()} selectLabel="Sign out">
            <Button size="2" variant="surface">
              Sign out
            </Button>
          </Focusable>
          <Focusable
            onSelect={() => void signOutEverywhere()}
            selectLabel="Sign out of all devices"
          >
            <Button size="2" variant="ghost">
              Sign out of all devices
            </Button>
          </Focusable>
        </div>
      </Card>

      <Card size="3">
        <Heading size="4">Delete account</Heading>
        {!confirmDelete ? (
          <Focusable onSelect={() => setConfirmDelete(true)} selectLabel="Delete account">
            <Button size="2" variant="ghost" color="danger">
              Delete account
            </Button>
          </Focusable>
        ) : (
          <>
            <Text render={<p />} size="2" color="gray">
              This removes your data here and takes your role off our Discord server. Your Discord
              account itself is separate and stays yours.
            </Text>
            <FocusableTextField
              value={deleteText}
              onChange={setDeleteText}
              label="Type your username to confirm"
              maxLength={64}
              autoFocus
            />
            <div className="gb-row" style={{ marginTop: '0.5rem' }}>
              <Focusable
                onSelect={() => deleteAccount.mutate()}
                selectLabel="Delete account"
                disabled={deleteText.trim() !== me.user.username}
              >
                <Button size="2" color="danger" disabled={deleteText.trim() !== me.user.username}>
                  Delete account
                </Button>
              </Focusable>
              <Focusable onSelect={() => setConfirmDelete(false)} selectLabel="Cancel">
                <Button size="2" variant="ghost">
                  Cancel
                </Button>
              </Focusable>
            </div>
          </>
        )}
      </Card>
    </FocusZone>
  );
}
